/**
 * 离散事件仿真内核主循环（设计文档 3.1 / F-012）。
 *
 * 纯 TS，零 DOM 依赖 —— 同一份代码既被主线程 SimSession 驱动可视化，
 * 也被 Node 无头脚本（批量实验 / CI 回归）直接引用（D2；内核本身 worker-agnostic）。
 * 确定性来源：种子化 PRNG + 整数毫秒时钟 + (time, seq) 事件排序 + 策略不直接改内核状态。
 */
import { EventQueue } from './event-queue';
import { resolveBoardingDirection } from './direction';
import { SimClock } from './clock';
import type { SimEvent } from './events';
import type { ExperimentConfig } from '../config-schema';
import { kinematicsFromScene } from '../config-schema';
import { createBuilding, type Building } from '../entities/building';
import { ElevatorCar } from '../entities/elevator-car';
import type { Passenger } from '../entities/passenger';
import type { HallCall } from '../entities/hall-call';
import { generatePassengers, type GeneratedPassenger } from '../traffic/passenger-gen';
import { profileBoundsMs } from '../traffic/traffic-profile';
import { MetricsCollector, type MetricsSummary, type SeriesPoint } from '../metrics/collector';
import type { SimulationSnapshot, CarSnapshot, FloorSnapshot } from '../snapshot';
import type { DispatchStrategy, Assignment, SnapshotView, CarView, CallView, InitContext, StrategyParams } from '../../strategy/strategy-api';
import { travelTimeForFloorsMs } from '../physics/kinematics';
import type { KinematicsParams } from '../physics/kinematics';

const ALLOCATION_TICK_MS = 10_000; // 派梯再评估周期
const MAX_EVENTS = 2_000_000; // 死锁兜底（正常机场级 ~1e4 事件，余量充足；过高会在 runaway 时先 OOM）
const SERIES_SAMPLE_MS = 5_000; // 指标时序采样间隔（仿真毫秒）

/** 策略切换等元事件（F-010 记入事件日志/导出）。 */
export interface MetaEntry {
  readonly type: 'strategy_switch';
  readonly simTimeMs: number;
  readonly fromId: string;
  readonly toId: string;
}

export interface RunResult {
  summary: MetricsSummary;
  eventCount: number;
  finalTimeMs: number;
  fingerprint: number;
  strandedCount: number;
}

export class Simulation {
  readonly clock = new SimClock();
  readonly queue = new EventQueue();
  metrics = new MetricsCollector();

  private building!: Building;
  private kinematics!: KinematicsParams; // 单一梯型，取首台
  private strategy!: DispatchStrategy;
  private config!: ExperimentConfig;
  private makeStrategy!: () => DispatchStrategy;
  /** 是否留存完整事件日志（默认不开，仅导出/回放需要时开，避免长仿真内存膨胀）。 */
  private keepLog = false;
  private readonly cars = new Map<string, ElevatorCar>();
  private readonly passengers = new Map<number, Passenger>();
  /** 楼层 → 候梯乘客索引（O(1) 登梯/快照查询，免去每次 O(P) 全量扫描）。 */
  private readonly waitingByFloor = new Map<number, Passenger[]>();
  private readonly calls = new Map<number, HallCall>(); // 未分配呼叫
  private readonly generated = new Map<number, GeneratedPassenger>();
  private readonly processed: SimEvent[] = [];
  private fingerprint = 0x51f5d2a8;
  private nextCallId = 0;
  private lastSampleMs = 0;
  /** 策略切换等元事件（导出用，不计入确定性指纹）。 */
  readonly metaLog: MetaEntry[] = [];
  /** 指标时序采样（F-018）。 */
  readonly metricsSeries: SeriesPoint[] = [];

  /** 初始化世界；makeStrategy 用于 reset 时重建确定性策略实例（F-013）。keepLog=true 才留存完整事件日志。 */
  init(config: ExperimentConfig, makeStrategy: () => DispatchStrategy, opts?: { keepLog?: boolean }): void {
    this.config = config;
    this.makeStrategy = makeStrategy;
    this.keepLog = opts?.keepLog ?? false;
    this.buildWorld();
  }

  /**
   * 重置到初始状态：同 config + 同种子重建世界（确定性重跑，AC-F013-03/AC-F006-01）。
   * 清空事件队列、时钟、实体、指标与指纹。
   */
  reset(): void {
    this.buildWorld();
  }

  /** 重建世界（init 与 reset 共用）。 */
  private buildWorld(): void {
    const config = this.config;
    this.queue.clear();
    this.clock.reset();
    this.cars.clear();
    this.passengers.clear();
    this.waitingByFloor.clear();
    this.calls.clear();
    this.generated.clear();
    this.processed.length = 0;
    this.fingerprint = 0x51f5d2a8;
    this.nextCallId = 0;
    this.lastSampleMs = 0;
    this.metaLog.length = 0;
    this.metricsSeries.length = 0;
    this.metrics = new MetricsCollector();

    this.building = createBuilding(config.scene.floors, config.scene.floorHeight, config.scene.lobbyFloor);
    this.kinematics = kinematicsFromScene(config.scene, config.scene.elevators[0]!);
    this.strategy = this.makeStrategy();

    for (const ec of config.scene.elevators) {
      this.cars.set(ec.id, new ElevatorCar(ec, config.scene.lobbyFloor));
    }

    this.strategy.init?.({
      seed: config.seed,
      kinematics: this.kinematics,
      floors: config.scene.floors,
      carCount: config.scene.elevators.length,
    } satisfies InitContext);

    // 预生成全时段到达事件（设计文档 5.2：eventQueue ← 全时段到达）
    const pax = generatePassengers(config.scene, config.traffic.profile, config.traffic.mode, config.seed);
    for (const p of pax) {
      this.generated.set(p.id, p);
      this.queue.enqueue(p.arrivalMs, (time, seq) => ({ type: 'PASSENGER_ARRIVAL', time, seq, passengerId: p.id }));
    }

    // 预调度派梯再评估周期（确定性，避免呼叫被遗忘）
    const { startMs, endMs } = profileBoundsMs({ segments: config.traffic.profile });
    for (let t = startMs; t <= endMs; t += ALLOCATION_TICK_MS) {
      this.queue.enqueue(t, (time, seq) => ({ type: 'ALLOCATION_TICK', time, seq }));
    }
  }

  /** 处理单条事件（F-013 单步推进）。队空返回 false。 */
  step(): boolean {
    return this.processOne() !== undefined;
  }

  /**
   * 推进到目标仿真时刻：处理所有 time ≤ targetMs 的事件（F-013 实时驱动 / 倍速）。
   * @returns 处理事件数与是否已耗尽
   */
  stepUntil(targetMs: number, maxBatch = 100_000): { processed: number; done: boolean } {
    let n = 0;
    while (this.queue.size > 0 && this.queue.peek()!.time <= targetMs) {
      if (++n > maxBatch) break;
      this.processOne();
    }
    return { processed: n, done: this.queue.size === 0 };
  }

  /** 处理一条事件（弹出→推进时钟→记指纹→分发→时序采样）。 */
  private processOne(): SimEvent | undefined {
    if (this.queue.size === 0) return undefined;
    const e = this.queue.pop()!;
    this.clock.advanceTo(e.time);
    if (this.keepLog) this.processed.push(e);
    this.fingerprint = cyrb53(eventKey(e), this.fingerprint);
    this.handle(e);
    this.sampleSeries();
    return e;
  }

  /** 按仿真时刻周期采样指标时序（F-018；确定性，同种子同序列）。 */
  private sampleSeries(): void {
    const now = this.clock.now();
    if (now - this.lastSampleMs < SERIES_SAMPLE_MS) return;
    this.lastSampleMs = now;
    const s = this.metrics.summaryAt(now);
    this.metricsSeries.push({
      tMs: now,
      avgWaitMs: s.avgWaitMs,
      waiting: s.waitingCount,
      throughputPerHour: s.throughputPerHour,
    });
  }

  /**
   * 运行中热切换策略（F-010）：记入元日志，新策略接管未分配呼叫（不丢事件，AC-F010-02），
   * 已在梯乘客不受影响。参数由 makeStrategy 闭包应用。
   */
  switchStrategy(makeStrategy: () => DispatchStrategy): void {
    const fromId = this.strategy.id;
    const next = makeStrategy();
    this.metaLog.push({ type: 'strategy_switch', simTimeMs: this.clock.now(), fromId, toId: next.id });
    this.strategy = next;
    this.strategy.init?.({
      seed: this.config.seed,
      kinematics: this.kinematics,
      floors: this.config.scene.floors,
      carCount: this.config.scene.elevators.length,
    } satisfies InitContext);
    this.allocate(); // 新策略立即接管未分配呼叫
  }

  /** 已处理事件日志（导出/回放用，AC-F021-02）。 */
  get eventLog(): readonly SimEvent[] {
    return this.processed;
  }

  /** 当前实验配置（导出快照用）。 */
  get experimentConfig(): ExperimentConfig {
    return this.config;
  }

  /** 运行至队列耗尽（无头脚本用）。 */
  run(maxEvents = MAX_EVENTS): RunResult {
    let count = 0;
    while (this.queue.size > 0) {
      if (++count > maxEvents) {
        throw new Error(`event budget exceeded (${maxEvents}); possible deadlock`);
      }
      this.processOne();
    }
    this.metrics.setSimEnd(this.clock.now());
    return {
      summary: this.metrics.summary(),
      eventCount: count,
      finalTimeMs: this.clock.now(),
      fingerprint: this.fingerprint,
      strandedCount: this.countStranded(),
    };
  }

  /** 暴露运动学参数（渲染插值用）。 */
  get kinematicsParams(): KinematicsParams {
    return this.kinematics;
  }

  /** 实时更新策略参数（F-009：下一次派梯即生效，AC-F009-01）。 */
  applyStrategyParams(params: StrategyParams): void {
    this.strategy.setParams?.(params);
  }

  /** 已送达乘客的乘梯记录（用于回放/方向不变式校验）。 */
  get rides(): readonly {
    originFloor: number;
    destFloor: number;
    direction: 1 | -1;
    carId: string;
    boardMs: number;
    alightMs: number;
  }[] {
    const out: { originFloor: number; destFloor: number; direction: 1 | -1; carId: string; boardMs: number; alightMs: number }[] = [];
    for (const p of this.passengers.values()) {
      if (p.state === 'delivered' && p.assignedCarId && p.boardMs != null && p.alightMs != null) {
        out.push({
          originFloor: p.originFloor,
          destFloor: p.destFloor,
          direction: p.direction,
          carId: p.assignedCarId,
          boardMs: p.boardMs,
          alightMs: p.alightMs,
        });
      }
    }
    return out;
  }

  private countStranded(): number {
    let n = 0;
    for (const p of this.passengers.values()) if (p.state !== 'delivered') n++;
    return n;
  }

  /** 登记候梯乘客到楼层索引。 */
  private addWaiting(p: Passenger): void {
    const arr = this.waitingByFloor.get(p.originFloor);
    if (arr) arr.push(p);
    else this.waitingByFloor.set(p.originFloor, [p]);
  }

  /** 从楼层索引移除已登梯乘客。 */
  private removeWaiting(p: Passenger): void {
    const arr = this.waitingByFloor.get(p.originFloor);
    if (!arr) return;
    const i = arr.indexOf(p);
    if (i >= 0) arr.splice(i, 1);
    if (arr.length === 0) this.waitingByFloor.delete(p.originFloor);
  }

  // ---- 事件分发 ----
  private handle(e: SimEvent): void {
    switch (e.type) {
      case 'PASSENGER_ARRIVAL': this.onArrival(e.passengerId); break;
      case 'ALLOCATION_TICK': this.allocate(); break;
      case 'CAR_ARRIVED': this.onCarArrived(e.carId, e.floor); break;
      case 'DOOR_OPENED': this.onDoorOpened(e.carId, e.floor); break;
      case 'DOOR_CLOSED': this.onDoorClosed(e.carId); break;
      case 'CAR_BECAME_IDLE': this.onIdle(e.carId); break;
    }
  }

  private onArrival(passengerId: number): void {
    const gen = this.generated.get(passengerId);
    if (!gen) return;
    const passenger: Passenger = {
      id: gen.id,
      arrivalMs: gen.arrivalMs,
      originFloor: gen.originFloor,
      destFloor: gen.destFloor,
      direction: gen.destFloor > gen.originFloor ? 1 : -1,
      state: 'waiting',
    };
    this.passengers.set(passengerId, passenger);
    this.addWaiting(passenger);
    const call: HallCall = {
      id: this.nextCallId++,
      passengerId,
      floor: gen.originFloor,
      direction: passenger.direction,
      createdAt: this.clock.now(),
    };
    this.calls.set(call.id, call);
    this.metrics.recordPassengerArrived();
    this.allocate();
  }

  // ---- 派梯 ----
  private allocate(): void {
    if (this.calls.size === 0) return;
    const view = this.buildView();
    const assignments = this.strategy.allocate(view);
    this.applyAssignments(assignments);
  }

  private buildView(): SnapshotView {
    const now = this.clock.now();
    const cars: CarView[] = [];
    for (const c of this.cars.values()) {
      cars.push({ id: c.id, floor: c.floor, state: c.state, direction: c.direction, load: c.load, capacity: c.capacity, pending: c.pending });
    }
    const calls: CallView[] = [];
    for (const call of this.calls.values()) {
      calls.push({ id: call.id, floor: call.floor, direction: call.direction, waitMs: now - call.createdAt });
    }
    return { simTimeMs: now, cars, calls, metrics: this.metrics.summary() };
  }

  private applyAssignments(assignments: readonly Assignment[]): void {
    const toDispatch = new Set<string>();
    for (const a of assignments) {
      const call = this.calls.get(a.callId);
      const car = this.cars.get(a.carId);
      if (!call || !car) continue;
      const passenger = this.passengers.get(call.passengerId);
      if (!passenger) {
        this.calls.delete(a.callId);
        continue;
      }
      car.addPickup(call.floor, call.direction);
      car.pending++;
      passenger.assignedCarId = car.id;
      this.calls.delete(a.callId);
      toDispatch.add(car.id);
    }
    for (const id of toDispatch) {
      const car = this.cars.get(id)!;
      if (car.state === 'idle' && car.hasStops) this.dispatch(id);
    }
  }

  /** 选择下一停靠楼层并调度 CAR_ARRIVED（或原地开门）。 */
  private dispatch(carId: string): void {
    const car = this.cars.get(carId);
    if (!car) return;
    const ns = car.nextStop();
    if (!ns) {
      // 理论不可达（hasStops 为真才进 dispatch）；防御置空闲并清方向，与 onDoorClosed 路径一致
      car.state = 'idle';
      car.direction = 0;
      return;
    }
    car.direction = ns.direction;
    const target = ns.floor;
    const now = this.clock.now();
    if (target === car.floor) {
      car.state = 'doorOpen';
      this.queue.enqueue(now, (time, seq) => ({ type: 'DOOR_OPENED', time, seq, carId, floor: target }));
      return;
    }
    const travel = travelTimeForFloorsMs(Math.abs(target - car.floor), this.kinematics);
    car.segFromFloor = car.floor;
    car.segToFloor = target;
    car.segStartMs = now;
    car.segEndMs = now + travel;
    car.segLoad = car.load;
    car.state = 'moving';
    this.queue.enqueue(now + travel, (time, seq) => ({ type: 'CAR_ARRIVED', time, seq, carId, floor: target }));
  }

  // ---- 轿厢事件 ----
  private onCarArrived(carId: string, floor: number): void {
    const car = this.cars.get(carId);
    if (!car) return;
    // 记录刚完成运行段的指标（载客=段出发时载客数）
    const runMs = this.clock.now() - car.segStartMs;
    const distanceM = Math.abs(car.segToFloor - car.segFromFloor) * this.building.floorHeight;
    const goingDown = car.segToFloor < car.segFromFloor;
    if (runMs > 0 && distanceM > 0) {
      this.metrics.recordSegment(car.capacity, car.segLoad, runMs, distanceM, goingDown, car.config.carMass);
    }
    car.floor = floor;
    // 保留承诺方向（A+B）：不在到层时清零，方向感知上客据此 gate
    car.state = 'doorOpen';
    this.queue.enqueue(this.clock.now() + car.config.doorOpenTime, (time, seq) => ({ type: 'DOOR_OPENED', time, seq, carId, floor }));
  }

  private onDoorOpened(carId: string, floor: number): void {
    const car = this.cars.get(carId);
    if (!car) return;
    car.floor = floor;

    // 下客
    const alighted: number[] = [];
    for (const [pid, dest] of car.riders) if (dest === floor) alighted.push(pid);
    for (const pid of alighted) {
      car.riders.delete(pid);
      const p = this.passengers.get(pid);
      if (p) {
        p.state = 'delivered';
        p.alightMs = this.clock.now();
        this.metrics.recordAlighted((p.alightMs ?? 0) - (p.boardMs ?? 0));
      }
    }

    // 方向感知上客（A+B）：依据 pickups 方向标记 + 轿内目的层解析承诺方向（无需扫描全部乘客）
    const pickupHere = car.pickups.get(floor);
    const upHere = pickupHere?.up ?? false;
    const downHere = pickupHere?.down ?? false;
    let upAhead = false;
    let downAhead = false;
    for (const d of car.riders.values()) {
      if (d > floor) upAhead = true;
      else if (d < floor) downAhead = true;
    }
    for (const [f, fl] of car.pickups) {
      if (f === floor) continue;
      if (f > floor && fl.up) upAhead = true;
      if (f < floor && fl.down) downAhead = true;
    }
    const dir = resolveBoardingDirection(car.direction, upAhead, downAhead, upHere, downHere);
    car.direction = dir;

    // 只接与承诺方向同向、本车指派的本层候梯乘客（按 id 升序、容量允许）
    const waiters = (this.waitingByFloor.get(floor) ?? []).filter(
      (p) => p.assignedCarId === carId && p.direction === dir,
    );
    waiters.sort((a, b) => a.id - b.id);
    let boarded = 0;
    for (const p of waiters) {
      if (car.load >= car.capacity) break;
      car.riders.set(p.id, p.destFloor);
      p.state = 'riding';
      p.boardMs = this.clock.now();
      this.metrics.recordBoarded((p.boardMs ?? 0) - p.arrivalMs);
      this.removeWaiting(p);
      boarded++;
    }
    car.pending = Math.max(0, car.pending - boarded);
    // 该层该方向已无本车候梯乘客 → 清除其接客标记（反向标记保留，待折返接走）
    const sameDirRemain = (this.waitingByFloor.get(floor) ?? []).some(
      (p) => p.assignedCarId === carId && p.direction === dir,
    );
    if (!sameDirRemain && dir !== 0) car.clearPickup(floor, dir);

    const now = this.clock.now();
    const dwell = car.config.doorCloseTime + boarded * car.config.boardTimePerPax + alighted.length * car.config.alightTimePerPax;
    this.queue.enqueue(now + dwell, (time, seq) => ({ type: 'DOOR_CLOSED', time, seq, carId, floor }));
  }

  private onDoorClosed(carId: string): void {
    const car = this.cars.get(carId);
    if (!car) return;
    if (car.hasStops) {
      this.dispatch(carId);
    } else {
      car.state = 'idle';
      car.direction = 0;
      this.queue.enqueue(this.clock.now(), (time, seq) => ({ type: 'CAR_BECAME_IDLE', time, seq, carId }));
    }
  }

  private onIdle(carId: string): void {
    const car = this.cars.get(carId);
    if (!car) return;
    car.state = 'idle';
    car.direction = 0;
    this.allocate(); // 空闲车可被重新指派
  }

  // ---- 快照（渲染用）----
  buildSnapshot(): SimulationSnapshot {
    const now = this.clock.now();
    const cars: CarSnapshot[] = [];
    for (const c of this.cars.values()) {
      cars.push({
        id: c.id, floor: c.floor, state: c.state, direction: c.direction,
        load: c.load, capacity: c.capacity,
        segFromFloor: c.segFromFloor, segToFloor: c.segToFloor,
        segStartMs: c.segStartMs, segEndMs: c.segEndMs,
      });
    }
    const floors: FloorSnapshot[] = [];
    for (let f = 0; f < this.building.floors; f++) {
      // 由楼层候梯索引直接聚合，O(候梯人数) 而非 O(楼层×乘客)
      let upCall = false;
      let downCall = false;
      let count = 0;
      let longest = 0;
      for (const p of this.waitingByFloor.get(f) ?? []) {
        count++;
        const w = now - p.arrivalMs;
        if (w > longest) longest = w;
        if (p.direction === 1) upCall = true;
        else downCall = true;
      }
      floors.push({ floor: f, upCall, downCall, waitingCount: count, longestWaitMs: longest });
    }
    return { simTimeMs: now, cars, metrics: this.metrics.summary(), floors };
  }
}

// ---- 确定性指纹（设计文档 3.8：事件日志逐字节哈希一致）----
function eventKey(e: SimEvent): string {
  switch (e.type) {
    case 'PASSENGER_ARRIVAL': return `A|${e.time}|${e.passengerId}`;
    case 'CAR_ARRIVED': return `CA|${e.time}|${e.carId}|${e.floor}`;
    case 'DOOR_OPENED': return `O|${e.time}|${e.carId}|${e.floor}`;
    case 'DOOR_CLOSED': return `C|${e.time}|${e.carId}|${e.floor}`;
    case 'CAR_BECAME_IDLE': return `I|${e.time}|${e.carId}`;
    case 'ALLOCATION_TICK': return `T|${e.time}`;
  }
}

/** cyrb53 —— 链式哈希：state_{n} = cyrb53(key_n, state_{n-1})，最终值依赖完整事件序列与顺序。 */
function cyrb53(str: string, seed: number): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
